import {
  ApiException,
  DocumentTemplateType,
  InputTemplateDto,
  InputTemplateType,
  MergeFieldDto,
  QueryFactory,
  TemplateDto,
} from '@lmt/shared-types';
import { useEventCallback } from '@mui/material';
import DocumentViewerHeader from '@lmt/unicorn-shared/app/components/documents/DocumentViewerHeader';
import { disablePrintAndDownload } from '@lmt/unicorn-shared/app/components/documents/richedit/disablePrintAndDownload';
import { ReadyForPdfButton } from '@lmt/unicorn-shared/app/components/documents/richedit/ReadyForPdfDocumentsButton';
import { SaveDocumentRichEditButton } from '@lmt/unicorn-shared/app/components/documents/richedit/SaveDocumentRichEditButton';
import { SaveTemplateAsRichEditButton } from '@lmt/unicorn-shared/app/components/documents/richedit/SaveTemplateAsRichEditButton';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import {
  getCurrentlyOpenedTabId,
  useTabs,
} from '@lmt/unicorn-shared/app/features/navigation/operations';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useQueryClient } from '@tanstack/react-query';
import { ContextMenuItem, FileTabCommandId } from 'devexpress-richedit';

import Logger from 'js-logger';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  NumberParam,
  StringParam,
  useQueryParams,
} from 'react-router-url-params';
import {
  DXR,
  Options,
  RichEdit,
} from '../../common/devexpress/RichEdit/dx-richedit';
import { RichEditComponent } from '../../common/devexpress/RichEdit/RichEditComponent';
import { Loading } from '../common/Loading';
import { mergeFieldCodeAsString } from './components/helpers';
import { MergeFieldList } from './components/MergeFieldList';

import localStyles from './DocumentViewer.module.scss';
import { DocumentViewerProps } from './DocumentViewerProps';
import { ActivateTemplateRichEditButton } from './richedit/ActivateTemplateRichEditButton';
import { DeleteTemplateRichEditButton } from './richedit/DeleteTemplateRichEditButton';
import { ImportExportTemplateButton } from './richedit/ImportExportTemplateButton';
import { OpenTemplatesRichEditMenuItem } from './richedit/OpenTemplatesRichEditMenuItem';
import { isDocumentNew } from './richedit/richedit-helper';
import { SaveTemplateRichEditButton } from './richedit/SaveTemplateRichEditButton';
import {
  ShowCodesAndValuesSwitcher,
  showFieldCodes,
  updateFieldsAsync,
} from './richedit/ShowCodesAndValuesSwitcher';
import { UpdateHeaderAndFooter } from './richedit/UpdateHeaderAndFooter';
import { DocumentViewerMode } from './types';
import { UpdateContextMenu } from './richedit/UpdateContextMenu';
import { CreateNewDocument } from './richedit/CreateNewDocument';

const manageTemplatesId = 'manageTemplatesId';

export const DocumentViewer: React.FC<DocumentViewerProps> = React.memo(
  function DocumentViewer(props) {
    const [
      { dataBlockId, caseId, visitId, documentId: documentIdParam },
      setQueryParams,
    ] = useQueryParams({
      dataBlockId: NumberParam,
      caseId: NumberParam,
      visitId: NumberParam,
      documentId: StringParam,
    });

    const tabs = useTabs();
    const queryClient = useQueryClient();
    const dispatch = useUniDispatch();
    const modals = useModal();
    const richEdit = useRef<RichEdit>(null!);
    const i18n = useTranslation();
    const [isRichEditLoaded, setIsRichEditLoaded] = useState(false);
    const claims = useClaims();
    const documentId =
      props.mode === DocumentViewerMode.Viewer ? documentIdParam : undefined;
    const patientId =
      props.mode === DocumentViewerMode.Viewer ? props.patientId : undefined;
    const fileInfoQuery = QueryFactory.FileQuery.useGetFileInfoQuery(
      { fileId: documentId! },
      {
        enabled: !!documentId,
      },
    );
    const patientQuery = QueryFactory.PatientQuery.useGetPatientQuery(
      patientId!,
      {
        enabled: !!patientId,
      },
    );
    const inputTemplatesQuery = QueryFactory.InputTemplateQuery.useGetQuery({
      type: InputTemplateType.Document,
      dataBlockFieldId: null,
      subtenantId: patientQuery.data?.subtenantId,
    });
    const inputTemplates: InputTemplateDto[] = useMemo(() => {
      return (inputTemplatesQuery.data ?? []).sort((a, b) =>
        a.name.localeCompare(b.name),
      );
    }, [inputTemplatesQuery.data]);

    const isLoading = useRef(true);

    const { templateType: templateTypeParam } =
      props.mode === DocumentViewerMode.Designer
        ? pages.documentDesigner.useParams()
        : { templateType: undefined };
    const templateType = templateTypeParam
      ? templateTypeParam
      : DocumentTemplateType.Document;

    const versionHistory = QueryFactory.FileQuery.useGetVersionHistoryQuery(
      fileInfoQuery.data?.groupId!,
      { enabled: !!fileInfoQuery.data?.groupId },
    );

    const isReadonly = versionHistory.data
      ? versionHistory.data[versionHistory.data.length - 1].id != documentId
      : false;

    const onLoaded = useCallback(() => {
      setIsRichEditLoaded(true);
      [
        FileTabCommandId.PrintDocument,
        // We need it to export the document
        //FileTabCommandId.DownloadDocumentMenu,
        FileTabCommandId.DownloadDocx,
        FileTabCommandId.DownloadRtf,
        FileTabCommandId.DownloadTxt,
      ].forEach((x) => richEdit.current.setCommandEnabled(x, false));
    }, []);
    const onInit = useCallback(
      (options: Options) => {
        options.ribbon.removeTab(DXR.RibbonTabType.View);
        options.ribbon.removeTab(DXR.RibbonTabType.MailMerge);
        const filePanel = options.ribbon.getTab(DXR.RibbonTabType.File)!;
        if (props.mode === DocumentViewerMode.Viewer) {
          filePanel.removeItem(DXR.FileTabItemId.CreateNewDocument);
          const contextMenu = options.contextMenu;
          if (claims.permissions.inputTemplateManagement) {
            contextMenu?.insertItem(
              new ContextMenuItem(manageTemplatesId, {
                text: i18n.t('documents.context_menu.manage_templates'),
                beginGroup: true,
              }),
            );
            options.events.customCommandExecuted = function (s, e) {
              if (
                claims.permissions.inputTemplateManagement &&
                e.commandName === manageTemplatesId
              ) {
                forms.openInputTemplatesEditing(
                  InputTemplateType.Document,
                  null,
                  false,
                  patientQuery.data?.subtenantId,
                );
              }
            };
          }
        } else {
          options.events.documentLoaded = (richEdit, e) => {
            if (!isLoading.current && isDocumentNew(richEdit)) {
              tabs.changeTabPath(
                pages.documentDesigner.link({
                  templateType,
                }),
                false,
              );
              setCurrentTemplate(null);
            }
            isLoading.current = false;
          };
        }
        filePanel.removeItem(DXR.FileTabItemId.OpenDocument);
        if (
          props.mode === DocumentViewerMode.Viewer &&
          documentId &&
          !claims.permissions.createOrEditDocuments
        ) {
          filePanel.removeItem(DXR.FileTabItemId.ExportDocument);
        }
        disablePrintAndDownload(options);
      },
      [inputTemplates, patientQuery],
    );

    useLayoutEffect(() => {
      if (isRichEditLoaded) {
        richEdit.current.readOnly = isReadonly;
      }
    }, [isRichEditLoaded, isReadonly]);

    const mergeFieldsQuery = QueryFactory.MergeFieldQuery.useGetQuery();
    const { data: mergeFields, isLoading: isMergeFieldsLoading } =
      mergeFieldsQuery;

    const [currentTemplate, setCurrentTemplate] = useState<TemplateDto | null>(
      null,
    );

    const onInsertMergeField = useCallback((mergeField: MergeFieldDto) => {
      const selection = richEdit.current.selection;
      const subDocument = selection.activeSubDocument;
      let position = selection.active;

      // if `position` is inside an existing DOCVARIABLE, insert new field AFTER existing
      const fieldsAtPosition = subDocument.fields.find(position);
      if (fieldsAtPosition.length > 0) {
        position = fieldsAtPosition[0].resultInterval.end + 1;
      }
      const mergeFieldCode = mergeFieldCodeAsString(mergeField);
      const interval = subDocument.fields.create(
        position,
        'DOCVARIABLE ' + mergeFieldCode,
      ).interval;
      selection.setSelection(interval.end);
      richEdit.current.focus();
    }, []);

    const forms = useGlobalForms();

    useEffect(
      function UpdateMergeFieldValuesInDocumentOnLoad() {
        if (!isRichEditLoaded) return;
        const handler = () => {
          if (!mergeFields) return;

          if (props.mode === DocumentViewerMode.Viewer) {
            if (documentId) {
              // we should not automatically update fields if we are loading a previously saved document
              richEdit.current.document.fields.showAllFieldResults(true);
            } else {
              updateFieldsAsync(i18n, richEdit.current, queryClient, forms, {
                patientId: props.patientId,
                caseId: caseId ?? null,
                visitId: visitId ?? null,
                dataBlockId: dataBlockId ?? null,
              }).catch((e) => Logger.error(e));
            }
          } else {
            showFieldCodes(richEdit.current, mergeFields);
          }
        };

        richEdit.current.events.documentLoaded.addHandler(handler);
        return () => {
          richEdit.current.events.documentLoaded.removeHandler(handler);
        };
      },
      [mergeFields, isRichEditLoaded, documentId],
    );

    const openDocument = useEventCallback(
      async (fileName: string, documentId: string, sender: RichEdit) => {
        sender.loadingPanel.show();
        sender.beginUpdate();
        try {
          const file = await QueryFactory.FileClient.downloadFile(documentId);
          sender.openDocument(file.data, fileName);
        } catch (e) {
          if (e instanceof ApiException) {
            if (e.status == 404) {
              await modals.showError({
                text: i18n.t('patients.documents.file_not_found'),
              });
            } else {
              await modals.showError({ text: convertToErrorString(e) });
            }
          } else {
            // When closing the tab during opening a document the following error could appear:
            // "Cannot read properties of null (reading 'commandManager')"
            // We ignore it.
            const errorString = convertToErrorString(e);
            if (
              !errorString.includes(
                "Cannot read properties of null (reading 'commandManager')",
              ) &&
              !errorString.includes(
                "Cannot read properties of null (reading 'modelManipulator')",
              )
            ) {
              await modals.showError({ text: errorString });
            }
          }
        } finally {
          try {
            sender.loadingPanel.hide();
            sender.endUpdate();
          } catch (e) {}
        }
      },
    );

    useEffect(
      function callOpenDocumentWhenComponentIsInitiallyLoaded() {
        if (
          props.mode === DocumentViewerMode.Viewer &&
          documentId &&
          fileInfoQuery.data &&
          isRichEditLoaded
        ) {
          openDocument(
            fileInfoQuery.data.name,
            documentId,
            richEdit.current,
          ).catch((e) => Logger.error(e));
        }
      },
      [fileInfoQuery.data, props.mode, openDocument, isRichEditLoaded],
    );

    const onTemplateOpened = useEventCallback(
      (template: TemplateDto | null) => {
        if (!template) {
          return;
        }
        setCurrentTemplate(template);

        const templateId = template.id;
        if (props.mode === DocumentViewerMode.Designer) {
          const path = pages.documentDesigner.link({
            templateId,
            templateType,
          });
          tabs.changeTabPath(path, false);
        } else {
          const path = pages.createPatientDocument.link(
            {
              patientId: patientId!,
              templateId: templateId,
            },
            { caseId, dataBlockId, visitId },
          );
          tabs.changeTabPath(path, false);
          setTimeout(() => {
            richEdit.current.hasUnsavedChanges = true;
          }, 1);
        }
      },
    );

    const resetCurrentDocument = useCallback(() => {
      setCurrentTemplate(null);
      tabs.changeTabPath(
        pages.documentDesigner.link({ templateId: 0, templateType }),
        false,
      );
    }, [templateType]);

    const onVersionSelect = useEventCallback(async (fileId: string) => {
      if (richEdit.current.hasUnsavedChanges) {
        const userSure = await modals.showConfirm({
          title: i18n.t('documents.version_change_warning.title'),
          text: i18n.t('documents.version_change_warning.text'),
        });
        if (userSure) {
          setQueryParams({ documentId: fileId });
        }
      } else {
        setQueryParams({ documentId: fileId });
      }
    });

    const onNewDocumentId = useEventCallback((newDocumentId: string) => {
      const inBackground = getCurrentlyOpenedTabId() !== tabs.tabId;
      if (props.mode !== DocumentViewerMode.Viewer) {
        return;
      }
      if (!documentId || inBackground) {
        const path = pages.createPatientDocument.link(
          {
            patientId: props.patientId!,
          },
          {
            caseId,
            dataBlockId,
            visitId,
            documentId: newDocumentId,
          },
        );
        tabs.changeTabPath(path, false, inBackground);
      } else {
        setQueryParams({ documentId: newDocumentId });
      }
    });

    return (
      <div
        className={
          props.mode === DocumentViewerMode.Designer
            ? localStyles.gridWrapperDesigner
            : documentId
              ? localStyles.gridWrapperViewerWithHeader
              : localStyles.gridWrapperViewer
        }
        data-test-id={
          props.mode === DocumentViewerMode.Designer
            ? 'document-designer'
            : 'document-viewer'
        }
      >
        {documentId && fileInfoQuery.data && (
          <DocumentViewerHeader
            documentId={documentId}
            onVersionChange={onVersionSelect}
          />
        )}
        <div className={localStyles.richeditWrapper}>
          <Suspense fallback={<Loading loading={true} />}>
            <RichEditComponent
              initialize={onInit}
              onLoaded={onLoaded}
              richEditRef={richEdit}
            />
          </Suspense>
          {isRichEditLoaded && (
            <>
              {props.mode === DocumentViewerMode.Designer && (
                <>
                  <ImportExportTemplateButton
                    richEdit={richEdit}
                    currentTemplate={currentTemplate}
                    templateType={templateType}
                    tab={DXR.RibbonTabType.File}
                  />
                </>
              )}
              {props.mode === DocumentViewerMode.Viewer && (
                <>
                  <ReadyForPdfButton
                    richEdit={richEdit}
                    tab={DXR.RibbonTabType.File}
                    dataBlockId={dataBlockId ?? undefined}
                    documentId={documentId ?? undefined}
                    groupId={fileInfoQuery.data?.groupId}
                    onNewDocumentId={onNewDocumentId}
                    templateId={
                      currentTemplate?.id ?? fileInfoQuery.data?.templateId
                    }
                    patientId={props.patientId}
                  />
                </>
              )}
              {props.mode === DocumentViewerMode.Viewer && (
                <>
                  <UpdateContextMenu
                    richEdit={richEdit}
                    inputTemplates={inputTemplates}
                  />
                </>
              )}
              {props.mode === DocumentViewerMode.Designer && (
                <>
                  <CreateNewDocument
                    richEdit={richEdit}
                    resetCurrentDocument={resetCurrentDocument}
                  />
                </>
              )}
              {!(
                props.mode === DocumentViewerMode.Viewer &&
                documentId &&
                !mergeFieldsQuery.isLoading
              ) && (
                <>
                  <OpenTemplatesRichEditMenuItem
                    richEdit={richEdit}
                    templateType={templateType}
                    templateId={props.templateId}
                    onTemplateOpened={onTemplateOpened}
                    mode={props.mode}
                    patientId={
                      props.mode === DocumentViewerMode.Viewer
                        ? props.patientId
                        : undefined
                    }
                  />
                </>
              )}
              {props.mode === DocumentViewerMode.Designer && (
                <>
                  <SaveTemplateRichEditButton
                    richEdit={richEdit}
                    templateType={templateType}
                    currentTemplate={currentTemplate}
                    setCurrentTemplate={onTemplateOpened}
                  />
                  <SaveTemplateAsRichEditButton richEdit={richEdit} />
                </>
              )}
              {props.mode === DocumentViewerMode.Viewer &&
                !(documentId && !claims.permissions.createOrEditDocuments) && (
                  <SaveDocumentRichEditButton
                    patientId={props.patientId}
                    richEdit={richEdit}
                    template={currentTemplate}
                    blockId={dataBlockId ?? undefined}
                    documentId={documentId ?? undefined}
                    groupId={fileInfoQuery.data?.groupId}
                    onNewDocumentId={onNewDocumentId}
                  />
                )}
              {props.mode === DocumentViewerMode.Viewer &&
                (documentId || props.templateId) &&
                claims.permissions.createOrEditDocuments && (
                  <ShowCodesAndValuesSwitcher
                    richEdit={richEdit}
                    mergeFields={mergeFields!}
                    patientId={props.patientId}
                    caseId={caseId ?? undefined}
                    dataBlockId={dataBlockId ?? undefined}
                    visitId={visitId ?? undefined}
                  />
                )}
              {props.mode === DocumentViewerMode.Viewer &&
                documentId &&
                claims.permissions.createOrEditDocuments &&
                fileInfoQuery.data?.templateId && (
                  <UpdateHeaderAndFooter
                    richEdit={richEdit}
                    patientId={props.patientId}
                    templateId={fileInfoQuery.data.templateId}
                  />
                )}
              {props.mode === DocumentViewerMode.Designer && (
                <DeleteTemplateRichEditButton
                  richEdit={richEdit}
                  currentTemplate={currentTemplate}
                  templateType={templateType}
                />
              )}
              {props.mode === DocumentViewerMode.Designer &&
                currentTemplate && (
                  <ActivateTemplateRichEditButton
                    richEdit={richEdit}
                    currentTemplate={currentTemplate}
                    templateType={templateType}
                    setTemplate={setCurrentTemplate}
                  />
                )}
              {/*{props.mode === DocumentViewerMode.Designer && (*/}
              {/*  <ShowCodesAndValuesSwitcher*/}
              {/*    richEdit={richEdit}*/}
              {/*    mergeFields={mergeFields.type === 'data' ? mergeFields.data : []}*/}
              {/*  />*/}
              {/*)}*/}
            </>
          )}
        </div>
        {props.mode === DocumentViewerMode.Designer && (
          <div className={localStyles.mergeFieldsWrapper}>
            <Loading
              loading={isMergeFieldsLoading}
              title={i18n.t('documents.merge_fields_loading')}
            >
              <Suspense
                fallback={
                  <Loading loading={true} title={i18n.t('load.loading')} />
                }
              >
                <MergeFieldList onInsertMergeField={onInsertMergeField} />
              </Suspense>
            </Loading>
          </div>
        )}
      </div>
    );
  },
);
